import type { ReactNode } from "react";
import type { ActionEffect, CardDefinition } from "../domain/types/index.js";
import { StatIcon } from "./StatIcon.js";
import { ZoneIcon } from "./ZoneIcon.js";

export type EffectKey = "progress" | "action" | "draw" | "fatigue" | "block";

type EffectSummary = {
  key: EffectKey;
  amount: number;
};

const EFFECT_ORDER: readonly EffectKey[] = ["progress", "action", "draw", "fatigue", "block"];

function collectEffect(effect: ActionEffect | undefined, totals: Record<EffectKey, number>): void {
  if (!effect) return;
  switch (effect.kind) {
    case "progress":
    case "block":
    case "fatigue":
      totals[effect.kind] += effect.amount;
      return;
    case "heal":
      totals.fatigue -= effect.amount;
      return;
    case "draw":
      totals.draw += effect.count;
      return;
    case "drawToHandSize":
      totals.action += effect.actions;
      return;
    case "drawAction":
      totals.draw += effect.draw;
      totals.action += effect.actions;
      return;
    case "cellar":
      totals.action += effect.actions;
      return;
    case "workerCycle":
      totals.draw += effect.draw;
      totals.action += effect.actions;
      return;
    case "stables":
      totals.draw += 3;
      totals.action += 1;
      return;
    case "compound":
      effect.effects.forEach((child) => collectEffect(child, totals));
      return;
    default:
      return;
  }
}

export function summarizeCardEffects(definition: CardDefinition): readonly EffectSummary[] {
  const totals: Record<EffectKey, number> = { progress: definition.progress ?? 0, action: 0, draw: 0, fatigue: 0, block: 0 };
  if (definition.heal) totals.fatigue -= definition.heal;
  collectEffect(definition.effect, totals);
  return EFFECT_ORDER
    .filter((key) => totals[key] !== 0)
    .map((key) => ({ key, amount: totals[key] }));
}

function effectLabel({ key, amount }: EffectSummary): string {
  const labels: Record<EffectKey, string> = {
    progress: "進捗",
    action: "行動",
    draw: "カード",
    fatigue: "疲労",
    block: "防御",
  };
  return `${labels[key]}${amount > 0 ? "+" : ""}${amount}`;
}

export function EffectIcon({ effect }: { effect: { key: EffectKey } }) {
  if (effect.key === "draw") return <ZoneIcon name="deck" />;
  return <StatIcon name={effect.key} />;
}

export function CardEffectSummary({ definition }: { definition: CardDefinition }) {
  const effects = summarizeCardEffects(definition);
  if (effects.length === 0) return null;
  return (
    <span className="card-effect-summary" aria-label={effects.map(effectLabel).join("、")}>
      {effects.map((effect) => (
        <span className={`card-effect is-${effect.key}`} key={effect.key} aria-hidden="true">
          <EffectIcon effect={effect} />
          <span className="card-effect-label">{effectLabel(effect).replace(/[+-]\d+$/, "")}</span>
          <b>{effect.amount > 0 ? "+" : ""}{effect.amount}</b>
        </span>
      ))}
    </span>
  );
}

/**
 * 数値として即読できない、カード固有の処理だけを本文欄へ送る。
 * 定型の数値効果は CardEffectSummary が担うため、二重に読ませない。
 */
const CARD_RULE_TEXT: Partial<Record<string, string>> = {
  "work-triage": "手札1枚を選択し、このPJが終わるまで山札から除外",
  "unresolved-task": "残留：終了時 疲労+1。施策として使用すると消滅",
  cellar: "任意枚数を捨て、同じ枚数を引く",
  "knowledge-share": "捨て札1枚を山札の上に置く",
  "field-judgment": "山札上を公開。施策なら任意で即時使用",
  "emergency-reassignment": "手札1枚を捨て、2枚引く。行動+1",
  "business-recovery": "手札が5枚になるまで引く。行動+1",
  meeting: "手札の施策1枚を選び、2回使用する",
  focus: "手札が7枚になるまで引く。引いた施策は任意で脇へ置く",
  quality: "上2枚を廃棄／捨て札／山札へ戻す",
  sendoff: "手札の労働者1枚を廃棄する",
  "team-building": "手札の労働者1枚につき1枚引く",
  stables: "労働者1枚を捨て、3枚引く。行動+1",
  inn: "2枚捨てる",
  "city-quarter": "手札の施策1枚につき1枚引く",
  "kings-court": "手札の施策1枚を選び、3回使用する",
  training: "手札の新卒または中堅1枚を1ランクアップさせる",
  "collaboration-meeting": "施策を選択し、それを2回使う",
  "mid-career-hire": "中堅労働者1枚を捨て札に加える",
  "mass-graduate-hiring": "手札に新卒3枚を加える",
  "placement-plan": "1枚引く。行動+1。使用後の手札が新卒と窓際だけなら、進捗+1、疲労-1",
  "new-hire-onboarding": "新卒以外を引くまで1枚ずつ引く。行動+1",
  "work-standardization": "手札の新卒1枚につき進捗+1。使用後の手札が新卒だけなら、さらに進捗+3",
  "spot-assignment": "1枚引く。行動+1。手札に窓際があるなら、進捗+2",
  "psychological-counseling": "手札の窓際1枚を廃棄し、ベテラン1枚を捨て札に加える",
  "special-task-team": "手札の窓際1枚を捨てる。2枚引く。行動+2。進捗+2",
  headhunting: "ベテラン1枚を捨て札に加える。窓際1枚を捨て札に加える",
  "ai-adoption": "このラウンドから、PJ終了まで毎ラウンド進捗+1",
  "advisor-contract": "このラウンドから、PJ終了まで毎ラウンド1枚引く",
  "crisis-management-office": "このラウンドから、PJ終了まで毎ラウンド防御+1",
  "deadline-negotiation": "このフェーズの労働者の進捗を0にする。工期+1",
};

export function cardRuleText(definition: CardDefinition): string | undefined {
  return CARD_RULE_TEXT[definition.id] ?? (definition.type === "task" ? definition.text : undefined);
}

/** 上段に数値を切り出すとコストや順序が誤って読めるカード。 */
export function hasCoupledCardEffect(definition: CardDefinition): boolean {
  return definition.id === "stables" || definition.id === "emergency-reassignment"
    || definition.id === "psychological-counseling" || definition.id === "special-task-team" || definition.id === "headhunting";
}

const INLINE_EFFECT_PATTERN = /(進捗|疲労|行動|防御)([+-]\d+)|(\d+)枚引く/g;

const EFFECT_KEY_BY_LABEL: Record<string, EffectKey> = {
  進捗: "progress",
  疲労: "fatigue",
  行動: "action",
  防御: "block",
};

/**
 * カード本文でも数値リソースを共通アイコンで表す。
 * 元の文章を aria-label に残し、見た目だけをコンパクトにする。
 */
export function CardEffectText({ text }: { text: string }) {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  for (const match of text.matchAll(INLINE_EFFECT_PATTERN)) {
    const index = match.index ?? 0;
    if (index > cursor) nodes.push(text.slice(cursor, index));

    const key = match[3] ? "draw" : EFFECT_KEY_BY_LABEL[match[1]];
    const amount = match[3] ? `+${match[3]}` : match[2];
    const label = match[3] ? "カード" : match[1];
    nodes.push(
      <span className={`inline-card-effect is-${key}`} aria-hidden="true" key={`${index}-${key}`}>
        <EffectIcon effect={{ key }} />
        <span className="inline-card-effect-label">{label}</span>
        <b>{amount}</b>
      </span>,
    );
    cursor = index + match[0].length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return <span className="card-effect-text" aria-label={text}>{nodes}</span>;
}
